import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { canAccessFeature } from '../utils/planAccess';
import LocationFortnoxBillingSection from '../components/LocationFortnoxBillingSection';
import '../components/LocationFortnoxBillingSection.css';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import { getUploadUrl } from '../utils/uploadUrl';
import { formatTaskStatus, formatTaskPriority } from '../utils/taskLabels';
import BannerImageField from '../components/BannerImageField';
import EmptyState from '../components/EmptyState';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import ColumnCustomizer from '../components/ColumnCustomizer';
import { useConfirm } from '../context/ConfirmContext';
import './Tasks.css';
import './Locations.css';

const Locations = () => {
  const { t } = useTranslation(['locations', 'common']);
  const confirm = useConfirm();
  const { user } = useContext(AuthContext);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    customerId: ''
  });
  const [bannerFile, setBannerFile] = useState(null);
  const [removeBanner, setRemoveBanner] = useState(false);
  const bannerInputRef = useRef(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [locationTasks, setLocationTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState({
    banner: true,
    name: true,
    address: true,
    customer: true,
    tasks: true,
    fortnox: true
  });
  const fortnoxBillingRef = useRef(null);
  const [taskCounts, setTaskCounts] = useState({});
  const [sorting, setSorting] = useState({ column: null, direction: null });

  const locationColumnLabels = useMemo(() => ({
    banner: t('columnBanner'),
    name: t('common:name'),
    address: t('addressLabel'),
    customer: t('columnCustomer'),
    tasks: t('columnTasks'),
    fortnox: t('columnFortnox')
  }), [t]);

  const showFortnoxBilling = canAccessFeature(user, 'customIntegrations');

  useEffect(() => {
    fetchLocations();
    fetchTaskCounts();
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/api/customers');
      setCustomers(response.data.data || []);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setCustomers([]);
    }
  };

  const fetchTaskCounts = async () => {
    try {
      const response = await axios.get('/api/tasks');
      const allTasks = response.data.data;
      const counts = {};
      allTasks.forEach((task) => {
        if (task.location_id) {
          counts[task.location_id] = (counts[task.location_id] || 0) + 1;
        }
      });
      setTaskCounts(counts);
    } catch (err) {
      console.error('Error fetching task counts:', err);
    }
  };

  const handleSort = (column) => {
    setSorting(prev => {
      if (prev.column === column) {
        if (prev.direction === 'asc') {
          return { column, direction: 'desc' };
        } else if (prev.direction === 'desc') {
          return { column: null, direction: null };
        }
      }
      return { column, direction: 'asc' };
    });
  };

  const renderSortableHeader = (column, label) => {
    const isSorted = sorting.column === column;
    const sortDirection = isSorted ? sorting.direction : null;
    
    return (
      <th 
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => handleSort(column)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>{label}</span>
          {sortDirection === 'asc' && (
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>arrow_upward</span>
          )}
          {sortDirection === 'desc' && (
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>arrow_downward</span>
          )}
        </div>
      </th>
    );
  };

  const sortLocations = (locationList) => {
    if (!sorting.column || !sorting.direction) {
      return locationList;
    }

    return [...locationList].sort((a, b) => {
      let aValue, bValue;

      switch (sorting.column) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'address':
          aValue = (a.address || '').toLowerCase();
          bValue = (b.address || '').toLowerCase();
          break;
        case 'customer':
          aValue = (a.customer_name || '').toLowerCase();
          bValue = (b.customer_name || '').toLowerCase();
          break;
        case 'tasks':
          aValue = taskCounts[a.id] || 0;
          bValue = taskCounts[b.id] || 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        return sorting.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sorting.direction === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }
    });
  };

  const handleRowClick = (location) => {
    handleOpenEdit(location);
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get('/api/locations');
      setLocations(response.data.data);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError(formatError(err) || t('failedFetch'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingLocation(null);
    setFormData({ name: '', address: '', customerId: '' });
    setBannerFile(null);
    setRemoveBanner(false);
    if (bannerInputRef.current) {
      bannerInputRef.current.value = '';
    }
    setError('');
    submittingRef.current = false;
    setSubmitting(false);
    setShowModal(true);
  };

  const handleOpenEdit = async (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name || '',
      address: location.address || '',
      customerId: location.customer_id || ''
    });
    setBannerFile(null);
    setRemoveBanner(false);
    if (bannerInputRef.current) {
      bannerInputRef.current.value = '';
    }
    setError('');
    submittingRef.current = false;
    setSubmitting(false);
    setShowModal(true);
    // Fetch tasks for this location
    await fetchTasksForLocation(location.id);
  };

  const fetchTasksForLocation = async (locationId) => {
    try {
      setLoadingTasks(true);
      const response = await axios.get('/api/tasks');
      const allTasks = response.data.data;
      // Filter tasks that are assigned to this location
      const tasksForLocation = allTasks.filter((task) => task.location_id === locationId);
      setLocationTasks(tasksForLocation);
    } catch (err) {
      console.error('Error fetching tasks for location:', err);
      setLocationTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLocation(null);
    setFormData({ name: '', address: '', customerId: '' });
    setBannerFile(null);
    setRemoveBanner(false);
    if (bannerInputRef.current) {
      bannerInputRef.current.value = '';
    }
    setError('');
    submittingRef.current = false;
    setSubmitting(false);
    setLocationTasks([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    setError('');
    setSubmitting(true);

    try {
      const payload = new FormData();
      payload.append('name', formData.name);
      payload.append('address', formData.address || '');
      payload.append('customerId', formData.customerId || '');
      if (bannerFile) {
        payload.append('banner', bannerFile);
      }
      if (removeBanner && editingLocation) {
        payload.append('removeBanner', 'true');
      }

      if (editingLocation) {
        await axios.put(`/api/locations/${editingLocation.id}`, payload);
        if (showFortnoxBilling && fortnoxBillingRef.current) {
          const billingSaved = await fortnoxBillingRef.current.save();
          if (!billingSaved) {
            await fetchLocations();
            setError(t('fortnoxBillingPartialSaveFailed'));
            return;
          }
        }
      } else {
        // For super_admin, backend accepts optional companyId; for admins it's ignored and their company is used
        if (user.role === 'super_admin' && user.company_id) {
          payload.append('companyId', user.company_id);
        }
        await axios.post('/api/locations', payload);
      }

      handleCloseModal();
      fetchLocations();
      fetchTaskCounts();
    } catch (err) {
      setError(formatError(err) || t('failedSave'));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm(t('confirmDelete'), { danger: true }))) {
      return false;
    }
    try {
      await axios.delete(`/api/locations/${id}`);
      fetchLocations();
      fetchTaskCounts();
      return true;
    } catch (err) {
      setError(formatError(err) || t('failedDelete'));
      return false;
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const sortedLocations = sortLocations(locations);

  return (
    <div className="locations">
      <div className="container container--wide main-content">
        <div className="page-header">
          <h1>{t('pageTitle')}</h1>
          <div className="page-header-actions">
            <ColumnCustomizer
              columnLabels={locationColumnLabels}
              visibleColumns={visibleColumns}
              onChange={(key, checked) => setVisibleColumns((prev) => ({ ...prev, [key]: checked }))}
            />
            {(user.role === 'super_admin' || user.role === 'admin') && (
              <button type="button" className="btn btn-primary tasks-header-create-btn" onClick={handleOpenCreate}>
                <span className="material-symbols-outlined" aria-hidden="true">add</span>
                {t('createLocation')}
              </button>
            )}
          </div>
        </div>

        {error && <div className="error">{error}</div>}

        {sortedLocations.length > 0 ? (
          <>
            <div className="tasks-list-table-wrap tasks-list-table-desktop">
              <table className="table tasks-list-table">
                <thead>
                  <tr>
                    {visibleColumns.banner && <th>{t('columnBanner')}</th>}
                    {visibleColumns.name && renderSortableHeader('name', t('common:name'))}
                    {visibleColumns.address && renderSortableHeader('address', t('addressLabel'))}
                    {visibleColumns.customer && renderSortableHeader('customer', t('columnCustomer'))}
                    {visibleColumns.tasks && renderSortableHeader('tasks', t('columnTasks'))}
                    {showFortnoxBilling && visibleColumns.fortnox && (
                      <th>{t('columnFortnox')}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {sortedLocations.map((loc) => (
                    <tr
                      key={loc.id}
                      className="tasks-list-table-row"
                      onClick={() => handleRowClick(loc)}
                    >
                      {visibleColumns.banner && (
                        <td onClick={(e) => e.stopPropagation()}>
                          {loc.banner_image_url ? (
                            <img
                              src={getUploadUrl(loc.banner_image_url)}
                              alt={t('bannerAlt', { name: loc.name })}
                              className="location-list-banner-thumb"
                            />
                          ) : (
                            <span className="location-list-banner-placeholder">—</span>
                          )}
                        </td>
                      )}
                      {visibleColumns.name && (
                        <td className="tasks-list-table-title">{loc.name}</td>
                      )}
                      {visibleColumns.address && <td>{loc.address || t('common:emptyCell')}</td>}
                      {visibleColumns.customer && (
                        <td>{loc.customer_name || t('common:emptyCell')}</td>
                      )}
                      {visibleColumns.tasks && <td>{taskCounts[loc.id] || 0}</td>}
                      {showFortnoxBilling && visibleColumns.fortnox && (
                        <td>
                          {loc.fortnoxBillingStatus === 'ready' && (
                            <span className="fortnox-status-badge ready">{t('fortnoxStatusReady')}</span>
                          )}
                          {loc.fortnoxBillingStatus === 'incomplete' && (
                            <span className="fortnox-status-badge incomplete">{t('fortnoxStatusIncomplete')}</span>
                          )}
                          {(!loc.fortnoxBillingStatus || loc.fortnoxBillingStatus === 'none') && (
                            <span className="fortnox-status-badge none">{t('fortnoxStatusNone')}</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="tasks-card-grid tasks-list-cards-mobile">
              {sortedLocations.map((loc) => {
                const fortnoxStatus = loc.fortnoxBillingStatus || 'none';
                return (
                  <article
                    key={loc.id}
                    className="tasks-redesign-card"
                    onClick={() => handleRowClick(loc)}
                  >
                    <div className="tasks-redesign-card-body">
                      <div className="tasks-redesign-card-top">
                        <h3 className="tasks-redesign-card-title">{loc.name}</h3>
                        {showFortnoxBilling && (
                          <span className={`fortnox-status-badge ${fortnoxStatus === 'ready' ? 'ready' : fortnoxStatus === 'incomplete' ? 'incomplete' : 'none'}`}>
                            {fortnoxStatus === 'ready' && t('fortnoxStatusReady')}
                            {fortnoxStatus === 'incomplete' && t('fortnoxStatusIncomplete')}
                            {(fortnoxStatus === 'none' || !loc.fortnoxBillingStatus) && t('fortnoxStatusNone')}
                          </span>
                        )}
                      </div>
                      <div className="tasks-redesign-card-meta">
                        {loc.address && (
                          <span className="tasks-redesign-meta-item">
                            <span className="material-symbols-outlined" aria-hidden="true">location_on</span>
                            {loc.address}
                          </span>
                        )}
                        {loc.customer_name && (
                          <span className="tasks-redesign-meta-item">
                            <span className="material-symbols-outlined" aria-hidden="true">contacts</span>
                            {loc.customer_name}
                          </span>
                        )}
                        <span className="tasks-redesign-meta-item">
                          <span className="material-symbols-outlined" aria-hidden="true">checklist</span>
                          {t('taskCount', { count: taskCounts[loc.id] || 0 })}
                        </span>
                      </div>
                      {(user.role === 'super_admin' || user.role === 'admin') && (
                        <div className="tasks-redesign-card-actions" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(loc.id)}
                          >
                            {t('common:delete')}
                          </button>
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        ) : (
          <EmptyState
            icon="location_on"
            title={t('noLocationsTitle')}
            description={t('noLocationsDescription')}
            actionLabel={(user.role === 'super_admin' || user.role === 'admin') ? t('createLocation') : undefined}
            onAction={(user.role === 'super_admin' || user.role === 'admin') ? handleOpenCreate : undefined}
          />
        )}

        <Modal
          isOpen={showModal}
          onClose={handleCloseModal}
          title={editingLocation ? t('editLocation') : t('createLocation')}
          className="locations-modal-panel"
        >
          <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>{t('nameRequired')}</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('addressLabel')}</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>{t('customerLabel')}</label>
                  <select
                    name="customerId"
                    value={formData.customerId || ''}
                    onChange={handleChange}
                  >
                    <option value="">{t('noCustomer')}</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                        {customer.organization_name ? ` (${customer.organization_name})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('bannerImage')}</label>
                  <BannerImageField
                    existingUrl={!removeBanner && editingLocation?.banner_image_url ? editingLocation.banner_image_url : null}
                    selectedFile={bannerFile}
                    onFileSelect={(file) => {
                      setBannerFile(file);
                      if (file) {
                        setRemoveBanner(false);
                      }
                    }}
                    onRemove={(editingLocation?.banner_image_url && !removeBanner) || bannerFile ? () => {
                      setBannerFile(null);
                      if (editingLocation?.banner_image_url) {
                        setRemoveBanner(true);
                      }
                      if (bannerInputRef.current) {
                        bannerInputRef.current.value = '';
                      }
                    } : null}
                    inputRef={bannerInputRef}
                    disabled={submitting}
                  />
                </div>
                {editingLocation && showFortnoxBilling && (
                  <LocationFortnoxBillingSection
                    ref={fortnoxBillingRef}
                    locationId={editingLocation.id}
                    disabled={submitting}
                  />
                )}
                {editingLocation && (
                  <div className="form-group">
                    <label>{t('tasksAtLocation')}</label>
                    {loadingTasks ? (
                      <p>{t('loadingTasks')}</p>
                    ) : locationTasks.length > 0 ? (
                      <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '10px' }}>
                        <table className="table" style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th>{t('columnTitle')}</th>
                              <th>{t('common:status')}</th>
                              <th>{t('taskLog:columnPriority')}</th>
                              <th>{t('columnAssignedTo')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {locationTasks.map((task) => (
                              <tr key={task.id}>
                                <td>
                                  <Link 
                                    to={`/tasks/${task.id}`}
                                    style={{ color: '#007bff', textDecoration: 'underline' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCloseModal();
                                    }}
                                  >
                                    {task.title}
                                  </Link>
                                </td>
                                <td>
                                  <span
                                    style={{
                                      display: 'inline-block',
                                      padding: '4px 12px',
                                      borderRadius: '12px',
                                      color: 'white',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      textTransform: 'capitalize',
                                      backgroundColor: 
                                        task.status === 'completed' ? '#28a745' :
                                        task.status === 'in_progress' ? '#007bff' :
                                        task.status === 'assigned' ? '#ffc107' :
                                        '#6c757d'
                                    }}
                                  >
                                    {formatTaskStatus(task.status, t)}
                                  </span>
                                </td>
                                <td>{formatTaskPriority(task.priority, t)}</td>
                                <td>{task.assigned_user_names || t('common:emptyCell')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p style={{ color: '#666', fontStyle: 'italic' }}>{t('noTasksAtLocation')}</p>
                    )}
                  </div>
                )}
                {error && <div className="error">{error}</div>}
                <div className="modal-actions">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting
                      ? (editingLocation ? t('saving') : t('creating'))
                      : t('common:save')}
                  </button>
                  {editingLocation && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={submitting}
                      onClick={async () => {
                        if (await handleDelete(editingLocation.id)) {
                          handleCloseModal();
                        }
                      }}
                    >
                      {t('common:delete')}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                    disabled={submitting}
                  >
                    {t('common:cancel')}
                  </button>
                </div>
              </form>
        </Modal>
      </div>
    </div>
  );
};

export default Locations;
